"use client"

import { useEffect, useRef, useState, ReactNode } from "react"

interface LazySectionProps {
  children: ReactNode
  fallback?: ReactNode
  rootMargin?: string
}

/**
 * LazySection component that only renders children when they're about to enter the viewport.
 * This provides better performance by deferring rendering until the user scrolls near the section.
 */
export default function LazySection({
  children,
  fallback = null,
  rootMargin = "200px", // Start loading 200px before the section enters viewport
}: LazySectionProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const currentRef = ref.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true)
          setHasBeenVisible(true)
          // Once visible, we can disconnect the observer
          if (currentRef) {
            observer.unobserve(currentRef)
          }
        }
      },
      {
        rootMargin,
        threshold: 0.1,
      }
    )

    if (currentRef) {
      observer.observe(currentRef)
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef)
      }
    }
  }, [rootMargin])

  return (
    <div ref={ref}>
      {hasBeenVisible ? children : fallback}
    </div>
  )
}

